import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, ChevronRight, Shield, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { SpurwechselLogo } from "@/components/SpurwechselLogo";

const fade = { initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

export default function B2BLandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background" data-testid="b2b-landing">
      {/* Nav */}
      <nav className="glass-nav sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <SpurwechselLogo size="small" />
            <Badge variant="secondary" className="font-body text-xs">für Beratungsstellen</Badge>
          </div>
          <div className="flex gap-3">
            <Button variant="ghost" className="font-body text-sm" onClick={() => navigate("/login")}>Anmelden</Button>
            <Button className="font-body text-xs sm:text-sm bg-primary text-primary-foreground whitespace-nowrap" onClick={() => navigate("/register")}>Kostenlos testen</Button>
          </div>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <section className="pt-16 pb-12 lg:pt-24 lg:pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fade} className="max-w-3xl">
            <p className="font-body text-sm text-primary font-medium mb-4">Für MPU-Vorbereitungsstellen</p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.15] mb-6">
              Ihre Teilnehmer sagen das Richtige — aber es klingt nicht echt?
            </h1>
            <p className="font-body text-base sm:text-lg text-muted-foreground leading-relaxed mb-4 max-w-2xl">
              Sie führen Sitzung um Sitzung. Erklären Grundlagen. Korrigieren Formulierungen. Und zwischen den Terminen passiert — nichts.
            </p>
            <p className="font-body text-base sm:text-lg font-medium leading-relaxed mb-8 max-w-2xl">
              Spurwechsel trainiert Ihre Teilnehmer zwischen den Sitzungen. Sie sehen den Fortschritt und wissen genau, wann ein persönliches Gespräch sinnvoll ist.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <Button size="lg" className="font-body text-base bg-primary text-primary-foreground hover:bg-primary/90 gap-2" onClick={() => navigate("/register")} data-testid="hero-cta">
                14 Tage kostenlos testen <ChevronRight className="w-4 h-4" />
              </Button>
              <Button size="lg" variant="outline" className="font-body text-base" onClick={() => document.getElementById("ablauf")?.scrollIntoView({ behavior: "smooth" })}>
                So funktioniert es
              </Button>
            </div>
            <p className="font-body text-xs text-muted-foreground">Kein Zahlungsmittel nötig · 3 Teilnehmer inklusive · DSGVO-konform</p>
          </motion.div>
        </div>
      </section>

      {/* ═══ PROBLEM: Kennen Sie das? ═══ */}
      <section className="py-14 bg-muted/30 border-y border-border/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 {...fade} className="font-heading text-2xl sm:text-3xl font-bold text-center mb-10">Kennen Sie das?</motion.h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Der Unvorbereitete", text: "Der Teilnehmer sitzt vor Ihnen und sagt: 'War halt eine Ausnahme. Ich musste irgendwie nach Hause kommen.' Sie verbringen 45 Minuten mit Grundlagen. Wieder." },
              { title: "Die Lücke", text: "Sie hatten ein gutes Gespräch. Zwei Wochen später kommt er zurück — und fängt von vorn an. Nichts geübt. Nichts reflektiert. Alles vergessen." },
              { title: "Der blinde Fleck", text: "Sie betreuen 15 Teilnehmer. Wer hängt bei Eigenverantwortung? Wen könnten Sie schon in die Simulation schicken? Sie wissen es nicht genau." },
            ].map((s, i) => (
              <motion.div key={i} {...fade} transition={{ duration: 0.5, delay: i * 0.1 }}>
                <Card className="border-border/50 h-full">
                  <CardContent className="pt-6 pb-5">
                    <p className="font-body text-sm font-semibold mb-2">{s.title}</p>
                    <p className="font-body text-sm text-muted-foreground leading-relaxed">{s.text}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ LÖSUNG ═══ */}
      <section className="py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fade}>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4">Genau hier setzt Spurwechsel an.</h2>
            <p className="font-body text-base text-muted-foreground leading-relaxed">
              Ein Trainingssystem, das Ihre Teilnehmer zwischen den Sitzungen strukturiert vorbereitet. Der Teilnehmer übt eigenständig — Vorfall schildern, Verantwortung übernehmen, Rückfallstrategien formulieren. Sie sehen in Echtzeit, wie weit jeder ist, und bekommen eine klare Empfehlung: Wer ist bereit für ein persönliches Gespräch.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ ABLAUF ═══ */}
      <section id="ablauf" className="py-14 bg-muted/30 border-y border-border/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 {...fade} className="font-heading text-2xl sm:text-3xl font-bold text-center mb-10">So arbeitet Ihre Stelle mit Spurwechsel</motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "1", title: "Teilnehmer startet", text: "Sie geben Ihrem Teilnehmer einen Link. Er registriert sich in 2 Minuten und beginnt mit dem Training. Kein Aufwand für Sie." },
              { step: "2", title: "Er übt eigenständig", text: "Der Teilnehmer spricht regelmäßig mit dem System und arbeitet gezielt an seinen Schwächen — Vorfall klar schildern, Verantwortung übernehmen, Fragen konkret beantworten." },
              { step: "3", title: "Sie sehen den Stand", text: "In Ihrem Dashboard sehen Sie sofort: Wer ist aktiv, wer hängt fest, wer ist bereit. Keine Tabellen — klare Empfehlungen in Berater-Sprache." },
              { step: "4", title: "Sie greifen ein — gezielt", text: "Das System sagt Ihnen: 'Max Müller ist bereit. Fokus: Rückfallprävention.' Sie sparen die Grundlagenarbeit und beraten dort, wo es wirklich zählt." },
            ].map((s, i) => (
              <motion.div key={i} {...fade} transition={{ duration: 0.5, delay: i * 0.1 }}>
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground font-heading text-lg font-bold flex items-center justify-center mx-auto mb-3">{s.step}</div>
                  <p className="font-body text-sm font-semibold mb-2">{s.title}</p>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">{s.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ VERGLEICH ═══ */}
      <section className="py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 {...fade} className="font-heading text-2xl sm:text-3xl font-bold text-center mb-10">Was sich für Ihre Stelle ändert</motion.h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              ["5-8 Sitzungen pro Teilnehmer", "3-4 Sitzungen — der Rest läuft digital"],
              ["Grundlagenarbeit in jeder Sitzung", "Teilnehmer kommt vorbereitet"],
              ["Zwischen Sitzungen passiert nichts", "Teilnehmer übt eigenständig weiter"],
              ["Kein Überblick bei 15+ Teilnehmern", "Dashboard zeigt Status auf einen Blick"],
              ["Wiederholte Erklärungen, gleiche Themen", "System erkennt Schwächen und arbeitet gezielt"],
              ["Erfolgsquote schwer messbar", "Klare Empfehlung: bereit oder nicht"],
            ].map(([ohne, mit], i) => (
              <motion.div key={i} {...fade} transition={{ duration: 0.4, delay: i * 0.05 }} className="flex gap-3 items-start p-3 rounded-lg bg-muted/30">
                <ArrowRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-body text-sm text-muted-foreground line-through">{ohne}</p>
                  <p className="font-body text-sm font-medium">{mit}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PRAXIS-BEISPIEL ═══ */}
      <section className="py-14 bg-muted/30 border-y border-border/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 {...fade} className="font-heading text-2xl sm:text-3xl font-bold text-center mb-8">Ein typischer Fall</motion.h2>
          <motion.div {...fade} className="space-y-6">
            <div>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">
                Herr Schmidt kommt zu Ihrer Stelle. Alkohol am Steuer, 1,4 Promille. Im ersten Gespräch sagt er: <em className="text-foreground font-medium">'War halt eine Ausnahme. Ich musste irgendwie nach Hause kommen. Normalerweise trinke ich nicht so viel."</em>
              </p>
            </div>
            <div className="space-y-3">
              {[
                { week: "Woche 1-2", label: "Grundlagen", color: "bg-orange-400", text: "Er trainiert mit dem System. Das stellt fest: Er verharmlost den Vorfall und weicht bei konkreten Fragen aus. Ihr Dashboard zeigt: Grundlagen — Verharmlost den Vorfall." },
                { week: "Woche 3-4", label: "In Bearbeitung", color: "bg-warning", text: "Vorfallschilderung wird klarer. Er sagt jetzt: 'Ich habe 6-7 Getränke getrunken und mich trotzdem ans Steuer gesetzt. Das war meine Entscheidung.' Dashboard: In Bearbeitung — Rückfallplan noch abstrakt." },
                { week: "Woche 5", label: "Bereit", color: "bg-success", text: "Das System meldet: 'Bereit für persönliches Gespräch. Fokus: Rückfallstrategien konkretisieren.' Er kann den Vorfall schildern, übernimmt Verantwortung, hat erste Strategien." },
              ].map((s, i) => (
                <div key={i} className="flex gap-3">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={`w-3 h-3 rounded-full ${s.color}`} />
                    {i < 2 && <div className="w-0.5 flex-1 bg-border mt-1" />}
                  </div>
                  <div className="pb-2">
                    <p className="font-body text-xs font-semibold">{s.week} <Badge variant="secondary" className="text-[10px] ml-1">{s.label}</Badge></p>
                    <p className="font-body text-sm text-muted-foreground leading-relaxed mt-1">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <Card className="border-success/30 bg-success/5">
              <CardContent className="pt-4 pb-3">
                <p className="font-body text-sm"><strong>Ihr Gespräch:</strong> Sie wissen vorher genau, wo Sie ansetzen müssen. Kein Grundlagen-Erklären. Kein Raten. Gezielte Beratung — von der ersten Minute an.</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section className="py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 {...fade} className="font-heading text-2xl sm:text-3xl font-bold text-center mb-3">Transparent. Kein Risiko.</motion.h2>
          <p className="font-body text-sm text-muted-foreground text-center mb-8 max-w-xl mx-auto">Testen Sie Spurwechsel 14 Tage kostenlos mit 3 Teilnehmern. Kein Zahlungsmittel nötig. Jederzeit kündbar.</p>

          {/* Comparison */}
          <motion.div {...fade}>
            <Card className="border-primary/20 bg-primary/5 mb-8">
              <CardContent className="pt-6 pb-4">
                <div className="grid sm:grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="font-body text-xs text-muted-foreground mb-1">Ohne digitale Vorbereitung</p>
                    <p className="font-body text-sm">10 Teilnehmer × 6 Sitzungen × €80</p>
                    <p className="font-heading text-xl font-bold">€4.800<span className="font-body text-sm text-muted-foreground font-normal">/Monat</span></p>
                  </div>
                  <div>
                    <p className="font-body text-xs text-primary font-medium mb-1">Mit Spurwechsel</p>
                    <p className="font-body text-sm">10 × 3 Sitzungen × €80 + System</p>
                    <p className="font-heading text-xl font-bold text-primary">€2.899<span className="font-body text-sm text-muted-foreground font-normal">/Monat</span></p>
                    <p className="font-body text-xs text-success font-medium mt-1">Ersparnis: rund €1.900/Monat</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Plans */}
          <div className="grid md:grid-cols-3 gap-5 mb-8">
            {[
              { name: "Starter", price: "99", teilnehmer: "bis 5", features: ["Dashboard + Empfehlungen", "Teilnehmer-Übersicht", "Status + Fortschritt"] },
              { name: "Professional", price: "199", teilnehmer: "bis 20", highlight: true, badge: "Empfohlen", features: ["Alles aus Starter", "Feedback-Einblicke", "Berater-Notizen + Protokoll", "Entwicklungsverlauf"] },
              { name: "Enterprise", price: "399", teilnehmer: "unbegrenzt", features: ["Alles aus Professional", "Mehrere Berater-Zugänge", "API-Zugang", "Dedizierter Ansprechpartner"] },
            ].map((p, i) => (
              <motion.div key={i} {...fade} transition={{ duration: 0.5, delay: i * 0.1 }}>
                <Card className={`border-border/50 relative h-full ${p.highlight ? "border-primary shadow-lg ring-1 ring-primary/20" : ""}`}>
                  {p.badge && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Badge className="bg-primary text-primary-foreground font-body text-xs px-3">{p.badge}</Badge></div>}
                  <CardContent className="pt-6 pb-5">
                    <p className="font-heading text-lg font-bold">{p.name}</p>
                    <p className="font-body text-xs text-muted-foreground mb-3">{p.teilnehmer} Teilnehmer</p>
                    <div className="mb-4">
                      <span className="font-heading text-3xl font-bold">€{p.price}</span>
                      <span className="font-body text-sm text-muted-foreground">/Monat</span>
                    </div>
                    <p className="font-body text-xs text-muted-foreground mb-4">+ €29,99 pro aktivem Teilnehmer</p>
                    <ul className="space-y-2 mb-5">
                      {p.features.map((f, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <Check className="w-3.5 h-3.5 text-success mt-0.5 flex-shrink-0" />
                          <span className="font-body text-sm">{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className={`w-full font-body ${p.highlight ? "bg-primary text-primary-foreground" : ""}`} variant={p.highlight ? "default" : "outline"} onClick={() => navigate("/register")}>
                      Kostenlos testen
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <p className="font-body text-xs text-muted-foreground text-center">Nur aktive Teilnehmer werden berechnet. Inaktiv seit 30+ Tagen = kostenlos.</p>
        </div>
      </section>

      {/* ═══ VERTRAUEN ═══ */}
      <section className="py-14 bg-muted/30 border-t border-border/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fade}>
            <h2 className="font-heading text-2xl font-bold mb-6">Datenschutz ernst gemeint</h2>
            <div className="grid sm:grid-cols-2 gap-4 text-left mb-8">
              {[
                "Ihre Teilnehmer bestimmen, was Sie sehen können",
                "Keine vollständigen Gesprächsverläufe — nur Fortschritt und Feedback",
                "Verbindung jederzeit trennbar — Teilnehmer behält seine Daten",
                "DSGVO-konform · AVV als Download · Deutsche Server",
              ].map((t, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="font-body text-sm">{t}</span>
                </div>
              ))}
            </div>
            <p className="font-body text-xs text-muted-foreground max-w-lg mx-auto">
              Spurwechsel ersetzt keine Beratung. Kein Gutachten. Keine Diagnose. Es ist ein Trainingssystem, das Ihre Arbeit ergänzt — nicht ersetzt. Ihre Expertise bleibt der entscheidende Faktor.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fade}>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4">Probieren Sie es im nächsten Fall direkt aus.</h2>
            <p className="font-body text-base text-muted-foreground mb-6">In 2 Minuten eingerichtet. 14 Tage kostenlos. 3 Teilnehmer inklusive.</p>
            <Button size="lg" className="font-body text-base bg-primary text-primary-foreground hover:bg-primary/90 gap-2" onClick={() => navigate("/register")} data-testid="final-cta">
              Jetzt kostenlos starten <ChevronRight className="w-4 h-4" />
            </Button>
            <p className="font-body text-xs text-muted-foreground mt-3">Kein Zahlungsmittel nötig · Jederzeit kündbar</p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <SpurwechselLogo size="small" />
          <div className="flex gap-4">
            <button onClick={() => navigate("/privacy")} className="font-body text-xs text-muted-foreground hover:text-foreground">Datenschutz</button>
            <button onClick={() => navigate("/wissen")} className="font-body text-xs text-muted-foreground hover:text-foreground">MPU-Wissen</button>
            <button onClick={() => navigate("/")} className="font-body text-xs text-muted-foreground hover:text-foreground">Für Teilnehmer</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
